import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  CheckCircle, Building, DollarSign, Clock, 
  User, Mail, Phone, FileText, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ApplyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    // Contact Info
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    // Business Info
    business_name: '',
    business_type: '',
    years_in_business: '',
    annual_revenue: '',
    number_of_employees: '',
    // Financing Info
    financing_type: '',
    amount_requested: '',
    purpose: '',
    // Additional
    credit_score_range: '',
    additional_info: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/api/financing-requests`, formData);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <MarketingLayout>
        <section className="py-20 bg-gradient-to-b from-emerald-50 to-white min-h-[70vh]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white rounded-2xl shadow-lg p-12">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Application Received!</h1>
              <p className="text-lg text-gray-600 mb-8">
                Thank you for your interest in financing. Our team will review your application and contact you within 1-2 business days.
              </p>
              <Button onClick={() => navigate('/')} className="bg-primary hover:bg-primary/90">
                Return to Home
              </Button>
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <section className="py-16 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Apply for Business Financing</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Complete the form below to start your financing application. Our team will review and contact you within 1-2 business days.
            </p>
          </div>

          <Card className="border-gray-100 shadow-lg">
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Contact Information */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <User className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        data-testid="apply-first-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        data-testid="apply-last-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="apply-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="apply-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Building className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-semibold text-gray-900">Business Information</h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="business_name">Business Name *</Label>
                      <Input
                        id="business_name"
                        name="business_name"
                        value={formData.business_name}
                        onChange={handleChange}
                        required
                        data-testid="apply-business-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="business_type">Business Type *</Label>
                      <select
                        id="business_type"
                        name="business_type"
                        value={formData.business_type}
                        onChange={handleChange}
                        required
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="apply-business-type"
                      >
                        <option value="">Select business type</option>
                        <option value="sole_proprietorship">Sole Proprietorship</option>
                        <option value="llc">LLC</option>
                        <option value="corporation">Corporation</option>
                        <option value="partnership">Partnership</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="years_in_business">Years in Business *</Label>
                      <select
                        id="years_in_business"
                        name="years_in_business"
                        value={formData.years_in_business}
                        onChange={handleChange}
                        required
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="apply-years-in-business"
                      >
                        <option value="">Select years</option>
                        <option value="less_than_1">Less than 1 year</option>
                        <option value="1_2">1-2 years</option>
                        <option value="3_5">3-5 years</option>
                        <option value="6_10">6-10 years</option>
                        <option value="more_than_10">More than 10 years</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="annual_revenue">Annual Revenue *</Label>
                      <select
                        id="annual_revenue"
                        name="annual_revenue"
                        value={formData.annual_revenue}
                        onChange={handleChange}
                        required
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="apply-annual-revenue"
                      >
                        <option value="">Select revenue range</option>
                        <option value="under_100k">Under $100,000</option>
                        <option value="100k_250k">$100,000 - $250,000</option>
                        <option value="250k_500k">$250,000 - $500,000</option>
                        <option value="500k_1m">$500,000 - $1,000,000</option>
                        <option value="1m_5m">$1,000,000 - $5,000,000</option>
                        <option value="over_5m">Over $5,000,000</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="number_of_employees">Number of Employees *</Label>
                      <select
                        id="number_of_employees"
                        name="number_of_employees"
                        value={formData.number_of_employees}
                        onChange={handleChange}
                        required
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="apply-employees"
                      >
                        <option value="">Select employees</option>
                        <option value="1_5">1-5</option>
                        <option value="6_10">6-10</option>
                        <option value="11_25">11-25</option>
                        <option value="26_50">26-50</option>
                        <option value="over_50">Over 50</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Financing Request */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <DollarSign className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-semibold text-gray-900">Financing Request</h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="financing_type">Type of Financing *</Label>
                      <select
                        id="financing_type"
                        name="financing_type"
                        value={formData.financing_type}
                        onChange={handleChange}
                        required
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="apply-financing-type"
                      >
                        <option value="">Select financing type</option>
                        <option value="working_capital">Working Capital Line of Credit</option>
                        <option value="term_loan">Term Loan</option>
                        <option value="equipment_financing">Equipment Financing</option>
                        <option value="not_sure">Not Sure - Need Guidance</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="amount_requested">Amount Requested *</Label>
                      <select
                        id="amount_requested"
                        name="amount_requested"
                        value={formData.amount_requested}
                        onChange={handleChange}
                        required
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="apply-amount-requested"
                      >
                        <option value="">Select amount</option>
                        <option value="under_25k">Under $25,000</option>
                        <option value="25k_50k">$25,000 - $50,000</option>
                        <option value="50k_100k">$50,000 - $100,000</option>
                        <option value="100k_250k">$100,000 - $250,000</option>
                        <option value="250k_500k">$250,000 - $500,000</option>
                        <option value="over_500k">Over $500,000</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="purpose">Purpose of Financing *</Label>
                      <select
                        id="purpose"
                        name="purpose"
                        value={formData.purpose}
                        onChange={handleChange}
                        required
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="apply-purpose"
                      >
                        <option value="">Select purpose</option>
                        <option value="equipment_purchase">Equipment Purchase</option>
                        <option value="vehicle_purchase">Vehicle/Truck Purchase</option>
                        <option value="working_capital">Working Capital / Cash Flow</option>
                        <option value="expansion">Business Expansion</option>
                        <option value="hiring">Hiring / Payroll</option>
                        <option value="inventory">Inventory / Materials</option>
                        <option value="debt_consolidation">Debt Consolidation</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="credit_score_range">Estimated Credit Score *</Label>
                      <select
                        id="credit_score_range"
                        name="credit_score_range"
                        value={formData.credit_score_range}
                        onChange={handleChange}
                        required
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="apply-credit-score"
                      >
                        <option value="">Select range</option>
                        <option value="excellent">Excellent (720+)</option>
                        <option value="good">Good (680-719)</option>
                        <option value="fair">Fair (620-679)</option>
                        <option value="challenged">Challenged (Below 620)</option>
                        <option value="unknown">Not Sure</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-semibold text-gray-900">Additional Information</h2>
                  </div>
                  <div>
                    <Label htmlFor="additional_info">Tell us more about your business and financing needs (optional)</Label>
                    <Textarea
                      id="additional_info"
                      name="additional_info"
                      value={formData.additional_info}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Share any additional details that would help us understand your needs better..."
                      data-testid="apply-additional-info"
                    />
                  </div>
                </div>

                {/* Submit */}
                <div className="pt-4 border-t">
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-primary hover:bg-primary/90"
                    disabled={loading}
                    data-testid="apply-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </Button>
                  <p className="text-sm text-gray-500 text-center mt-4">
                    By submitting this form, you agree to be contacted about business financing options.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default ApplyPage;
